import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getDamageStatusesAPI } from "services/api/fleet/vehicles/damages/api";
import { initialFleetVehicleDamageSearchForm } from "services/api/fleet/vehicles/damages/consts";
import { DamageStatus } from "services/api/fleet/vehicles/damages/types";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";
import { TextField } from "ui/components/TextField/TextField";

import { DamageSearchFiltersProps } from "./types";

const DamageSearchFilters: React.FC<DamageSearchFiltersProps> = (props) => {
  const { formValues, onSearchChange } = props;

  const { t } = useTranslate();

  const [statuses, setStatuses] = useState<DamageStatus[]>([]);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setStatuses(await getDamageStatusesAPI());
    } catch {
      setStatuses([]);
    }
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onSearchChange({ ...formValues, search: e.target.value });
    },
    [formValues, onSearchChange]
  );

  const handleStatusChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleDamageStatusId: item.value || null,
      });
    },
    [formValues, onSearchChange]
  );

  // Promjena marke prazni model i tip, promjena modela prazni tip — inače ostane
  // nemoguća kombinacija (model jedne marke uz drugu marku) i popis se isprazni.
  const handleManufacturerChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleManufacturerId: item.value || null,
        vehicleModelId: null,
        vehicleTypeId: null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleModelChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        vehicleModelId: item.value || null,
        vehicleTypeId: null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleTypeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({ ...formValues, vehicleTypeId: item.value || null });
    },
    [formValues, onSearchChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onSearchChange({ ...formValues, [name]: value });
    },
    [formValues, onSearchChange]
  );

  const handleResetFormClick = useCallback(() => {
    onSearchChange(initialFleetVehicleDamageSearchForm);
  }, [onSearchChange]);

  const statusItems: DropdownOption[] = statuses.map((status) => ({
    value: status.id,
    text: status.name,
  }));

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[220px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder="Naziv štete..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="min-w-[180px]">
        <Dropdown
          name="vehicleDamageStatusId"
          placeholder="Status"
          options={statusItems}
          value={formValues.vehicleDamageStatusId}
          onChange={handleStatusChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleManufacturerDropdown
          name="vehicleManufacturerId"
          placeholder={t("admin.vehicle.manufacturer.placeholder")}
          value={formValues.vehicleManufacturerId}
          onChange={handleManufacturerChange}
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleModelDropdown
          name="vehicleModelId"
          placeholder={t("admin.vehicle.model.placeholder")}
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          value={formValues.vehicleModelId}
          onChange={handleModelChange}
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <VehicleTypeDropdown
          name="vehicleTypeId"
          placeholder={t("admin.vehicle.type.placeholder")}
          vehicleManufacturerId={formValues.vehicleManufacturerId}
          vehicleModelId={formValues.vehicleModelId}
          value={formValues.vehicleTypeId}
          onChange={handleTypeChange}
          width="full"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="damageDateFrom"
          label="Datum štete od - do"
          value={formValues.damageDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="damageDateTo"
          value={formValues.damageDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(DamageSearchFilters);
